import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Card, Chip, Stack, Typography } from '@mui/material';

import { SectionTitle } from '~/components/common/SectionTitle';
import type { ContractReview, ContractReviewRuleCategory } from '~/components/contractReview/types';
import { formatReviewRuleCategory } from '~/components/contractReview/types';
import { BUTTON_SX } from '~/theme';

const reviewRuleColor: Record<
  ContractReviewRuleCategory,
  'success' | 'error' | 'warning' | 'info' | 'default'
> = {
  'included-service': 'success',
  'excluded-service': 'error',
  'miscellaneous-charge': 'warning',
  'review-rule': 'info',
  'other-term': 'default',
};

/** What is covered first, then what is not, then the things that qualify both. */
const reviewRuleCategoryOrder: ContractReviewRuleCategory[] = [
  'included-service',
  'excluded-service',
  'miscellaneous-charge',
  'review-rule',
  'other-term',
];

/**
 * The rules the AI invoice review is held to — what this contract includes, excludes,
 * and charges extra for. A read-only card at the bottom of the drawer's Coverage tab
 * before; on the page it is a tab of its own, with Add rule, because it decides whether
 * an invoice gets flagged and so is something a consultant maintains.
 */
export function ContractReviewRulesTab({
  contract,
  onAddRule,
}: {
  contract: ContractReview;
  /** Opens the contract form on its Invoice Review Rules section. `null` for a reader who can't edit contracts. */
  onAddRule: (() => void) | null;
}) {
  const { reviewRules } = contract;

  const grouped = reviewRuleCategoryOrder
    .map((category) => ({
      category,
      rules: reviewRules.filter((rule) => rule.category === category),
    }))
    .filter(({ rules }) => rules.length > 0);

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Card variant="outlined" sx={{ p: 2 }}>
        <SectionTitle>Rules ({reviewRules.length})</SectionTitle>
        {onAddRule ? (
          <Box sx={{ mt: 1.5 }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={onAddRule}
              sx={BUTTON_SX}
              variant="outlined"
            >
              Add rule
            </Button>
          </Box>
        ) : null}

        {grouped.length > 0 ? (
          <Stack spacing={2} sx={{ mt: 1.5 }}>
            {grouped.map(({ category, rules }) => (
              <Box key={category}>
                <Chip
                  size="small"
                  color={reviewRuleColor[category]}
                  label={`${formatReviewRuleCategory(category)} (${rules.length})`}
                  variant="outlined"
                />
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  {rules.map((rule) => (
                    <Box key={rule.id}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {rule.rule}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            No review rules extracted from this contract yet.
          </Typography>
        )}
      </Card>
    </Box>
  );
}
